import type { Task } from '@timato/shared';
import { TaskDetails, uuidv7 } from '@timato/shared';
import { db } from '@/db/db';
import { isWork } from './stats';
import { deleteRow, notDeleted, saveRow } from './repo';

/** Recompute a task's cached logged time from its sessions. Called after any session write touching the task. */
export async function recomputeTrackedSec(taskId: string | null) {
  if (!taskId) return;
  const task = await db.tasks.get(taskId);
  if (!task) return;
  const sessions = (await db.sessions.where('taskId').equals(taskId).toArray()).filter((s) => notDeleted(s) && isWork(s));
  const trackedSec = sessions.reduce((sum, s) => sum + s.durationSec, 0);
  if (trackedSec !== task.trackedSec) await saveRow('tasks', { ...task, trackedSec });
}

/** Labels a session inherits when it starts: the task's plus the project's (8e). */
export async function labelsForGoal(taskId: string | null, projectId: string | null): Promise<string[]> {
  const [task, project] = await Promise.all([taskId ? db.tasks.get(taskId) : undefined, projectId ? db.projects.get(projectId) : undefined]);
  return [...new Set([...(task?.labelIds ?? []), ...(project?.labelIds ?? [])])];
}

export async function createTask(projectId: string, name: string, estimateSec: number | null = null, labelIds: string[] = []): Promise<Task> {
  const position = await db.tasks.where('projectId').equals(projectId).count();
  return saveRow('tasks', { id: uuidv7(), projectId, name: name.trim(), estimateSec, trackedSec: 0, labelIds, finishedAt: null, position, claimedBy: null, claimedAt: null, details: TaskDetails.parse({}), updatedAt: '', deletedAt: null });
}

/**
 * The project's open task with this name (case and spacing ignored), or a new one. A goal typed into Add session
 * becomes the same thing the timer's goal field offers (§3.2), so it can be picked up again there and its time counts
 * towards the task.
 *
 * The timer labels a session from its task and project (`labelsForGoal`), so the labels chosen in the dialog are
 * given to the task too, less the project's own: a new task starts with them, and one that has none of its own
 * adopts them. A task whose labels someone set is left as it is.
 */
export async function findOrCreateTask(projectId: string, name: string, labelIds: string[] = []): Promise<Task> {
  const key = name.trim().toLowerCase();
  const [tasks, project] = await Promise.all([db.tasks.where('projectId').equals(projectId).toArray(), db.projects.get(projectId)]);
  const own = labelIds.filter((id) => !project?.labelIds.includes(id));
  const existing = tasks.find((t) => notDeleted(t) && !t.finishedAt && t.name.trim().toLowerCase() === key);
  if (!existing) return createTask(projectId, name, null, own);
  return existing.labelIds.length || !own.length ? existing : saveRow('tasks', { ...existing, labelIds: own });
}

export const updateTask = (t: Task, patch: Partial<Pick<Task, 'name' | 'estimateSec' | 'labelIds' | 'projectId' | 'details'>>) => saveRow('tasks', { ...t, ...patch });
export const setTaskFinished = (t: Task, finished: boolean) => saveRow('tasks', { ...t, finishedAt: finished ? new Date().toISOString() : null });
export const deleteTask = (t: Task) => deleteRow('tasks', t);

/** Persist a new order for a project's open tasks (drag to reorder). */
export async function reorderTasks(ordered: Task[]) {
  await db.transaction('rw', [db.tasks, db.outbox], async () => {
    for (const [i, t] of ordered.entries()) if (t.position !== i) await saveRow('tasks', { ...t, position: i });
  });
}

/** Open tasks first by position, finished ones after, struck through in the UI (spec §3.4). */
export const sortTasks = (tasks: Task[]) =>
  [...tasks].sort((a, b) => Number(!!a.finishedAt) - Number(!!b.finishedAt) || a.position - b.position || a.updatedAt.localeCompare(b.updatedAt));

/**
 * A project's list in two parts (TW-26): open tasks in their dragged order, done ones most recently finished first,
 * since what was just ticked off is what someone comes back to check or reopen.
 */
export function splitTasks(tasks: Task[]) {
  const open = tasks.filter((t) => !t.finishedAt);
  const done = tasks.filter((t) => !!t.finishedAt).sort((a, b) => b.finishedAt!.localeCompare(a.finishedAt!));
  return { open, done };
}
